import { useTranslation } from 'react-i18next';

import { TDeviceSelectOptionsProps } from '@/console/features/devices/hooks/use-device-select-options/use-device-select-options.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { DeviceFragment } from '@/shared/types/types.generated';

export const useDeviceSelectOptions = (props?: TDeviceSelectOptionsProps) => {
  const { t } = useTranslation();
  const { getVendor } = useVendor();
  const { additionalInfo, disabled } = props || {};

  const isDisabled = (device: DeviceFragment) => {
    return (
      disabled?.notYetSupported?.(device) ||
      disabled?.noLinkedProject?.(device) ||
      disabled?.noProtocolConfiguration?.(device) ||
      false
    );
  };

  const getAdditionalInfo = (device: DeviceFragment) => {
    if (disabled?.notYetSupported?.(device)) {
      return t('common:not_yet_supported');
    }

    if (disabled?.noLinkedProject?.(device)) {
      return t('common:no_linked_project');
    }

    if (disabled?.noProtocolConfiguration?.(device)) {
      return t('common:no_protocol_configuration');
    }

    const infos = [];
    if (additionalInfo?.deviceType) {
      infos.push(t(`device:device_types.${device.deviceType.toLowerCase()}`));
    }
    if (additionalInfo?.ipAddress) {
      infos.push(device.connectionConfiguration.ipAddress);
    }
    if (additionalInfo?.vendor && getVendor(device.vendorId)?.name) {
      infos.push(getVendor(device.vendorId)?.name);
    }
    return infos;
  };

  const createDeviceOptions = (device: DeviceFragment) => {
    return {
      label: device.name,
      value: {
        name: device.name,
        deviceId: device.deviceId,
        objectVersion: device.objectVersion ?? 0,
      },
      additionalInfo: getAdditionalInfo(device),
      disabled: isDisabled(device),
    } as TSelectOption<DeviceFragment>;
  };

  return { createDeviceOptions, getAdditionalInfo, isDisabled };
};
